'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { PayloadImage } from '@/components/PayloadImage';

export function HeroBlockRenderer({ block }: { block: any }) {
  const variant = block.variant || 'gradient';

  if (variant === 'typewriter') return <HeroTypewriter block={block} />;
  if (variant === 'centeredCards') return <HeroCenteredCards block={block} />;
  if (variant === 'splitCream') return <HeroSplitCream block={block} />;
  return <HeroGradient block={block} />;
}

/* ── Typewriter + Mobile App Hero (old homepage style) ────────── */
function HeroTypewriter({ block }: { block: any }) {
  const words: string[] = (block.rotatingWords || []).map((r: any) => r.word).filter(Boolean);
  const defaultWords = ['Records', 'Documents', 'Timesheet', 'Attendance', 'Payroll', 'Performance', 'Assets'];
  const rotatingWords = words.length > 0 ? words : defaultWords;

  const [wordIndex, setWordIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const closeVideo = useCallback(() => setShowVideo(false), []);

  useEffect(() => {
    if (!showVideo) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeVideo(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [showVideo, closeVideo]);

  useEffect(() => {
    const currentWord = rotatingWords[wordIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        setDisplayText(currentWord.slice(0, displayText.length + 1));
        if (displayText.length + 1 === currentWord.length) {
          setTimeout(() => setIsDeleting(true), 1800);
        }
      } else {
        setDisplayText(currentWord.slice(0, displayText.length - 1));
        if (displayText.length - 1 === 0) {
          setIsDeleting(false);
          setWordIndex((prev) => (prev + 1) % rotatingWords.length);
        }
      }
    }, isDeleting ? 50 : 120);
    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, wordIndex, rotatingWords]);

  const prefix = block.headingPrefix || "Manage your employee's";
  const trustBadges = block.trustBadges || [
    { platform: 'Software Advice', score: '5-star rating', stars: 5 },
    { platform: 'Capterra', score: '5-star rating', stars: 5 },
    { platform: 'Google Reviews', score: '4.8 stars · 13k+', stars: 5 },
  ];
  const videoUrl = block.videoUrl || 'https://www.youtube-nocookie.com/embed/5Ocf4Q-ocBk?autoplay=1&rel=0';
  const videoLabel = block.videoLabel || 'Take a product tour';
  const showEmailCapture = block.showEmailCapture !== false;

  return (
    <section
      className="relative pt-24 pb-0 lg:pt-32 lg:pb-0 overflow-hidden"
      style={{ background: 'var(--t-gradient, var(--color-t-primary))' }}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Left — content */}
          <div className="text-center lg:text-left pb-12 lg:pb-20">
            {/* Rating eyebrow badge */}
            <div className="inline-flex items-center gap-2 mb-8">
              <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                <span className="text-white text-xs font-bold">G</span>
              </div>
              <span className="text-white/80 text-sm">
                <span className="font-semibold text-white">4.8 stars</span> 13k+ reviews
              </span>
            </div>

            {/* Heading with typewriter */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.1] mb-6 tracking-tight">
              {prefix}{' '}
              <span className="inline-block min-w-[4ch]" style={{ color: 'var(--color-t-accent, #f59e0b)' }}>
                {displayText}
                <span className="animate-pulse">|</span>
              </span>
            </h1>

            {/* Subheading */}
            {block.subheading && (
              <p className="text-lg text-white/60 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                {block.subheading}
              </p>
            )}

            {/* Email capture or plain CTA */}
            {showEmailCapture ? (
              <>
                <form
                  action={block.ctaLink || '/setup-free-account'}
                  className="flex flex-col sm:flex-row max-w-lg mx-auto lg:mx-0 mb-6 rounded-xl p-1.5 border border-white/10"
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)' }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const email = emailRef.current?.value;
                    const dest = block.ctaLink || '/setup-free-account';
                    window.location.href = `${dest}${email ? `?email=${encodeURIComponent(email)}` : ''}`;
                  }}
                >
                  <input
                    ref={emailRef}
                    type="email"
                    placeholder="Work email address*"
                    className="flex-1 px-5 py-3.5 bg-transparent text-white placeholder:text-white/40 text-sm outline-none min-w-0"
                  />
                  <button
                    type="submit"
                    className="px-8 py-3.5 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer"
                    style={{ backgroundColor: 'var(--color-t-cta)', color: 'var(--color-t-cta-text, #fff)' }}
                  >
                    {block.ctaLabel || 'Start Free'}
                  </button>
                </form>
                <p className="text-xs text-white/30 mb-10 max-w-lg mx-auto lg:mx-0">
                  By clicking &ldquo;{block.ctaLabel || 'Start Free'}&rdquo;, you agree to our{' '}
                  <a href="/privacy-policy" className="underline hover:text-white/50">Privacy Policy</a>.
                </p>
              </>
            ) : (
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 mb-10">
                {block.ctaLabel && block.ctaLink && (
                  <Link href={block.ctaLink} className="px-8 py-3.5 rounded-lg font-semibold text-sm shadow-lg" style={{ backgroundColor: 'var(--color-t-cta)', color: 'var(--color-t-cta-text, #fff)' }}>
                    {block.ctaLabel}
                  </Link>
                )}
                {block.secondaryCtaLabel && block.secondaryCtaLink && (
                  <Link href={block.secondaryCtaLink} className="px-8 py-3.5 rounded-lg font-semibold text-sm border border-white/30 text-white hover:bg-white/10 transition-colors">
                    {block.secondaryCtaLabel}
                  </Link>
                )}
              </div>
            )}

            {/* Video play button */}
            <button
              onClick={() => setShowVideo(true)}
              className="inline-flex items-center gap-3 mb-10 group cursor-pointer"
            >
              <div className="w-10 h-10 rounded-full border-2 border-white/30 flex items-center justify-center group-hover:border-white/60 transition-colors">
                <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              <span className="text-white/70 text-sm group-hover:text-white transition-colors">
                {videoLabel}
              </span>
            </button>

            {/* Trust badges */}
            {trustBadges.length > 0 && (
              <div className="flex flex-wrap items-center gap-8 justify-center lg:justify-start">
                {trustBadges.map((badge: any, i: number) => (
                  <div key={i} className="text-center">
                    <div className="flex items-center justify-center gap-0.5 mb-1">
                      {Array.from({ length: 5 }).map((_, s) => (
                        <svg
                          key={s}
                          className="w-4 h-4"
                          style={{ color: s < (badge.stars ?? 5) ? 'var(--color-t-accent, #f59e0b)' : 'rgba(255,255,255,0.2)' }}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <p className="text-white font-semibold text-xs">{badge.platform}</p>
                    <p className="text-white/40 text-[11px]">{badge.score}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right — mobile app image */}
          <div className="relative hidden lg:flex items-end justify-center">
            {block.image?.url ? (
              <PayloadImage
                media={block.image}
                alt={block.image.alt || 'OfficePortal app'}
                targetWidth={800}
                sizes="(min-width: 1024px) 40vw, 100vw"
                className="w-full max-w-md h-auto drop-shadow-2xl"
                priority
              />
            ) : (
              /* Fallback: static public image from old homepage */
              <Image
                src="/Officeportal-on-mobile.png"
                alt="OfficePortal HRMS mobile app"
                width={480}
                height={600}
                className="w-full max-w-md h-auto drop-shadow-2xl"
                priority
              />
            )}
          </div>
        </div>
      </div>

      {/* Video lightbox */}
      {showVideo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={closeVideo}
        >
          <button
            onClick={closeVideo}
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            aria-label="Close video"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div
            className="w-full max-w-4xl mx-4 aspect-video rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              className="w-full h-full"
              src={videoUrl}
              title="Product Tour"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
}

/* ── Gradient Hero (Layout B style) ── */
function HeroGradient({ block }: { block: any }) {
  return (
    <section
      className="relative pt-28 pb-16 lg:pb-24 overflow-hidden"
      style={{ background: 'var(--t-gradient, linear-gradient(135deg, var(--color-t-primary) 0%, var(--color-t-primary-dark) 100%))' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
        <div className="flex-1 text-center lg:text-left">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white mb-4 leading-tight">
            {block.heading}
          </h1>
          {block.subheading && (
            <p className="text-base sm:text-lg text-white/70 max-w-xl mb-6">{block.subheading}</p>
          )}
          {block.stats && block.stats.length > 0 && (
            <div className="flex flex-wrap gap-6 mb-8 justify-center lg:justify-start">
              {block.stats.map((s: any, i: number) => (
                <div key={i} className="text-center">
                  <div className="text-2xl font-extrabold" style={{ color: 'var(--color-t-cta)' }}>{s.value}</div>
                  <div className="text-xs text-white/50">{s.label}</div>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
            {block.ctaLabel && block.ctaLink && (
              <Link href={block.ctaLink} className="px-8 py-3 rounded-lg font-semibold text-sm shadow-lg" style={{ backgroundColor: 'var(--color-t-cta)', color: 'var(--color-t-cta-text)' }}>
                {block.ctaLabel}
              </Link>
            )}
            {block.secondaryCtaLabel && block.secondaryCtaLink && (
              <Link href={block.secondaryCtaLink} className="px-8 py-3 rounded-lg font-semibold text-sm border border-white/30 text-white hover:bg-white/10 transition-colors">
                {block.secondaryCtaLabel}
              </Link>
            )}
          </div>
        </div>
        {block.image?.url && (
          <div className="flex-1">
            <PayloadImage media={block.image} alt={block.image.alt || block.heading} targetWidth={1600} sizes="(min-width: 1024px) 50vw, 100vw" className="w-full h-auto rounded-xl shadow-2xl" priority />
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Split Cream Hero (Layout C style) ── */
function HeroSplitCream({ block }: { block: any }) {
  return (
    <section className="relative pt-28 pb-16 lg:pb-24 overflow-hidden" style={{ backgroundColor: 'var(--color-t-surface)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
        <div className="flex-1 text-center lg:text-left">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold mb-4 leading-tight" style={{ color: 'var(--color-t-heading)' }}>
            {block.heading}
          </h1>
          {block.subheading && (
            <p className="text-base sm:text-lg max-w-xl mb-6" style={{ color: 'var(--color-t-body)' }}>{block.subheading}</p>
          )}
          {block.stats && block.stats.length > 0 && (
            <div className="flex flex-wrap gap-6 mb-8 justify-center lg:justify-start">
              {block.stats.map((s: any, i: number) => (
                <div key={i} className="text-center">
                  <div className="text-2xl font-extrabold" style={{ color: 'var(--color-t-primary)' }}>{s.value}</div>
                  <div className="text-xs" style={{ color: 'var(--color-t-body)' }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
            {block.ctaLabel && block.ctaLink && (
              <Link href={block.ctaLink} className="px-8 py-3 rounded-lg font-semibold text-sm shadow-lg text-white" style={{ backgroundColor: 'var(--color-t-primary)' }}>
                {block.ctaLabel}
              </Link>
            )}
            {block.secondaryCtaLabel && block.secondaryCtaLink && (
              <Link href={block.secondaryCtaLink} className="px-8 py-3 rounded-lg font-semibold text-sm border-2 transition-colors" style={{ borderColor: 'var(--color-t-primary)', color: 'var(--color-t-primary)' }}>
                {block.secondaryCtaLabel}
              </Link>
            )}
          </div>
        </div>
        {block.image?.url && (
          <div className="flex-1">
            <PayloadImage media={block.image} alt={block.image.alt || block.heading} targetWidth={1600} sizes="(min-width: 1024px) 50vw, 100vw" className="w-full h-auto rounded-2xl shadow-xl" priority />
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Centered Cards Hero (Layout D style) ── */
function HeroCenteredCards({ block }: { block: any }) {
  return (
    <section className="relative pt-28 pb-16 overflow-hidden bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {block.pills && block.pills.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {block.pills.map((p: any, i: number) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: 'var(--color-t-surface)', color: 'var(--color-t-primary)' }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: p.color === 'cta' ? 'var(--color-t-cta)' : p.color === 'green' ? '#27CA40' : 'var(--color-t-primary)' }} />
                {p.label}
              </span>
            ))}
          </div>
        )}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold mb-5 leading-tight max-w-3xl mx-auto" style={{ color: 'var(--color-t-heading)' }}>
          {block.heading}
        </h1>
        {block.subheading && (
          <p className="text-base sm:text-lg max-w-xl mx-auto mb-8" style={{ color: 'var(--color-t-body)' }}>{block.subheading}</p>
        )}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-12">
          {block.ctaLabel && block.ctaLink && (
            <Link href={block.ctaLink} className="px-8 py-3 rounded-lg font-semibold text-sm shadow-lg text-white" style={{ backgroundColor: 'var(--color-t-primary)' }}>
              {block.ctaLabel}
            </Link>
          )}
          {block.secondaryCtaLabel && block.secondaryCtaLink && (
            <Link href={block.secondaryCtaLink} className="px-8 py-3 rounded-lg font-semibold text-sm border-2 transition-colors" style={{ borderColor: 'color-mix(in srgb, var(--color-t-primary) 20%, transparent)', color: 'var(--color-t-heading)' }}>
              {block.secondaryCtaLabel}
            </Link>
          )}
        </div>
        {block.image?.url && (
          <div className="max-w-4xl mx-auto">
            <PayloadImage media={block.image} alt={block.image.alt || block.heading} targetWidth={1920} sizes="(min-width: 1024px) 1024px, 100vw" className="w-full h-auto rounded-2xl shadow-xl" priority />
          </div>
        )}
      </div>
    </section>
  );
}
